<template>
  <div>
    <label v-if="label" class="mr-1 text-size-14 text-mild">{{ label }}</label>
    <AppLabel v-if="appLabelText" variant="red" data-testid="app-label-text">{{
      appLabelText
    }}</AppLabel>
    <div
      v-if="description"
      class="mr-1 text-size-12 text-mild"
      data-testid="description"
    >
      {{ description }}
    </div>
    <div
      :class="[
        `radio-group flex-${flexDirection} d-flex mt-2`,
        { stacked: variant === 'stacked' }
      ]"
    >
      <div
        v-for="option in options"
        :key="option.value"
        class="radio-group__item"
      >
        <AppRadio
          :disabled="disabled"
          :name="name"
          v-model="option.value"
          :id="`${name}-option-${option.value}`"
          :text="option.text"
          :checked="value === option.value"
          @change="onChange"
          :has-border="hasBorder"
          :size="size"
        />
      </div>
    </div>
    <FormInvalidFeedback
      v-if="validationState"
      :validation-state="validationState"
      :state="false"
    />
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-facing-decorator";
import AppLabel from "@/components/atoms/input/AppLabel.vue";
import AppRadio from "@/components/atoms/input/AppRadio.vue";
import FormInvalidFeedback from "@/components/atoms/other/FormInvalidFeedback.vue";

type ValidationStateType = {
  validated: boolean;
  valid: boolean | undefined;
};

type ButtonSize = "normal" | "small";
type FlexDirection = "wrap" | "column";

type RadioOption = {
  value: string | number;
  text: string;
};

@Component({
  components: {
    AppLabel,
    AppRadio,
    FormInvalidFeedback
  }
})
export default class FormRadioGroup extends Vue {
  @Prop({
    type: [String, Number],
    required: false,
    default: ""
  })
  value!: string | number;

  @Prop({
    type: Array,
    required: true,
    default: []
  })
  options!: RadioOption[];

  @Prop({
    type: String,
    required: true,
    default: ""
  })
  name!: string;

  @Prop({
    type: String,
    default: ""
  })
  label!: string;

  @Prop({
    type: String,
    default: ""
  })
  appLabelText!: string;

  @Prop({
    type: Boolean,
    default: false
  })
  disabled!: boolean;

  @Prop({
    type: String,
    default: "normal"
  })
  size!: ButtonSize;

  @Prop({
    type: Boolean,
    default: true
  })
  hasBorder!: boolean;

  @Prop({
    type: String,
    default: "wrap"
  })
  flexDirection!: FlexDirection;

  @Prop({
    type: Object,
    default: null
  })
  validationState!: ValidationStateType;

  @Prop({
    type: String,
    required: false
  })
  description!: string;

  @Prop({
    type: String,
    default: ""
  })
  variant!: string;

  onChange(v: string) {
    this.$emit("input", v);
  }
}
</script>

<style lang="scss" scoped>
.radio-group {
  flex-wrap: wrap;
  margin-right: -8px;
  margin-left: -8px;

  &__item {
    flex-grow: 1;
    margin: 0 8px;
  }

  // スタック表示用バリアント
  &.stacked {
    display: block !important;
    margin: 0 !important;

    .radio-group__item {
      margin: 0;

      &:has(input:checked) {
        background-color: #f3fcff;

        &:first-child {
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
        }

        &:last-child {
          border-bottom: none;
          border-bottom-right-radius: 8px;
          border-bottom-left-radius: 8px;
        }
      }

      &:last-child {
        :deep(label) {
          border-bottom: none !important;
        }
      }

      :deep(.app-radio__label) {
        padding: 16px;
        border-bottom: 1px solid #e5e5e5;

        &.border {
          border-top: none !important;
          border-right: none !important;
          border-left: none !important;
        }
      }
    }
  }
}
</style>
